import React, { useMemo, useState } from 'react';

import FormSection from '../VendorList/FormSection';
import { Checkbox, DatePicker, ExportModal } from '../uiold';
import { Button, CheckboxList, Stack, VStack } from '../../core-ui';
import { useExport } from '../../support/useExport';
import { Tag } from '../../types/shared/Tag';
import { TagLabelView } from '../Vendors/VendorTag';

import { BillingExportMarketDateFilter } from './BillingExportMarketDateFilter';
import { Market } from '../../runtypes/MarketRuntype';
import {
  getEmptySelectedMarketDates,
  SelectedMarketDatesMap,
  stringifySelectedMarketDates,
} from '../../core/support/selectedMarketDates';

type MarketSubset = Pick<Market, 'id' | 'dates' | 'name'>;

type Props = {
  markets: Array<MarketSubset>;
  onDismiss: () => void;
  tags: Array<Tag>;
};

export type SelectedDateRange = [] | [string] | [string, string];

export default function VendorBillingExportDialog(props: Props) {
  let { onDismiss, markets, tags } = props;
  let [dateRange, setDateRange] = useState<SelectedDateRange>([]);
  let [filterBalanceOwed, setFilterBalanceOwed] = useState(false);
  let [includeDetails, setIncludeDetails] = useState(false);
  const [selectedTags, setSelectedTags] = useState(new Set<string>());
  const [showMarketDateFilter, setShowMarketDateFilter] = useState(false);
  const [
    selectedMarketDates,
    setSelectedMarketDates,
  ] = useState<SelectedMarketDatesMap>(getEmptySelectedMarketDates(markets));

  let [doExport, { isLoading }] = useExport('/api/vendors/billing/export', {
    onSuccess: () => {
      onDismiss();
    },
  });

  const sortedTags = useMemo(() => {
    return tags.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [tags]);

  return (
    <ExportModal
      title={t('Export Vendor Billing Data')}
      isLoading={isLoading}
      onSubmit={() => {
        doExport({
          filters: {
            balanceOwed: filterBalanceOwed,
            dateRange: dateRange.length === 2 ? dateRange : undefined,
            selectedMarketDates:
              showMarketDateFilter && dateRange.length === 2
                ? stringifySelectedMarketDates(selectedMarketDates)
                : undefined,
            tags: Array.from(selectedTags),
          },
          includeDetails,
        });
      }}
      onClose={() => {
        onDismiss();
      }}
    >
      <FormSection
        title={t('Filters')}
        subtitle={t('Include only vendors with ...')}
      >
        <VStack alignItems="start">
          <Checkbox
            isChecked={filterBalanceOwed}
            onChange={(value) => setFilterBalanceOwed(value)}
            text={t('Balance owed')}
          />
        </VStack>
      </FormSection>
      {sortedTags.length ? (
        <FormSection
          title={t('Tags')}
          subtitle={t('Include only vendors with selected tags')}
        >
          <CheckboxList
            items={sortedTags}
            getKey={(tag) => tag.id}
            renderLabel={(tag) => <TagLabelView tag={tag} />}
            selected={selectedTags}
            onSelectionChange={setSelectedTags}
          />
        </FormSection>
      ) : null}
      <FormSection title={t('Additional Details')}>
        <Checkbox
          isChecked={includeDetails}
          onChange={(value) => setIncludeDetails(value)}
          text={t('Detailed activity')}
        />
      </FormSection>
      <FormSection
        title={t('Date Range')}
        subtitle={t(
          'Include only vendors with activity in selected date range',
        )}
      >
        <DatePicker
          selectedDates={new Set(dateRange)}
          onDateClick={(date) => {
            setDateRange((range) => {
              if (range.length !== 1) {
                setSelectedMarketDates(getEmptySelectedMarketDates(markets));
                setShowMarketDateFilter(false);
              }
              return range.length === 1 ? [...range, date] : [date];
            });
          }}
          isDateRange
        />
        {dateRange.length === 2 && (
          <Stack direction="row" spacing={4} justify="center">
            <Button variant="link" onClick={() => setDateRange([])}>
              {t('Clear Dates')}
            </Button>
          </Stack>
        )}
        <Checkbox
          isChecked={showMarketDateFilter && dateRange.length === 2}
          isDisabled={
            // Disable if no date range due to how billing activity is obtained in backend
            dateRange.length !== 2
          }
          onChange={(value) => {
            if (value === true) {
              setShowMarketDateFilter(true);
            } else if (value === false) {
              // Wipe market date selection when closing accordion
              setSelectedMarketDates(getEmptySelectedMarketDates(markets));
              setShowMarketDateFilter(false);
            }
          }}
          text={t('Filter by market date')}
        />
        {showMarketDateFilter && (
          <BillingExportMarketDateFilter
            markets={markets}
            selectedMarketDates={selectedMarketDates}
            setSelectedMarketDates={setSelectedMarketDates}
          />
        )}
      </FormSection>
    </ExportModal>
  );
}
